import { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, FormControl, Select, MenuItem,
  InputLabel, Grid, Stepper, Step, StepLabel, FormHelperText, Card, CardMedia, CardActions,
  Typography, CircularProgress, Alert, FormControlLabel, Switch
} from '@mui/material';
import Cropper from 'react-easy-crop';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { getReferences } from '../../services/SuppliesService';
import { getCategoriesByType } from '../../services/CategoryService';
import { addProduct, updateProduct, getProductById, getProductsAndPreparationsLight, getAllLocationsLiteralE } from './../../services/ProductService';
import { getSuppliers } from '../../services/SupplierService';
import ProductImagesService from '../../services/ProductImagesService';
import ProductItemManager from './../../components/ProductItem/ProductItemManager';
import PluginsContext from '../../context/PluginsContext';
import { COMPANY_TYPE, categoryOptions, referenceTypes, unitOptions, getUnitById } from '../../utils/Enums';
import { getDefaultProductTypeForCompany } from '../../utils/ProductTypeUtils';
import { readFile, getCroppedImg, createResizedImageFile } from '../../utils/ImageUtils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Tooltip, IconButton } from '@mui/material';

import CurrencySelect from '../Currency/CurrencySelect';
import { DEFAULT_CURRENCY } from '../../utils/currency';
const resolveStockControlMode = (product) =>
  product?.stockControlMode || product?.stock_control_mode ||
  (product?.tubIceCream ? 'FRACTIONAL' : 'UNITARY');

const AddEditProductModal = ({ locationId, open, onClose, onSave, product }) => {

  const { t } = useTranslation();
  const pluginsContext = useContext(PluginsContext);
  const isRetailCompany = Number(localStorage.getItem('companyType')) === COMPANY_TYPE.RETAIL;
  const defaultProductType = getDefaultProductTypeForCompany(localStorage.getItem('companyType'));
  // Las referencias incluyen insumos/preparaciones y sólo existen cuando está
  // habilitado Food Operations. No debemos consultar esa ruta para Retail.
  const canLoadFoodReferences = Boolean(
    !isRetailCompany &&
    pluginsContext?.hasPlugin('food_operations')
  );
  const contentDefinedManagerRef = useRef();
  const contentUndefinedManagerRef = useRef();
  const packagingManagerRef = useRef();
  const toppingManagerRef = useRef();
  const contentManagerRef = useRef();

  // Eliminado: pedidosYaActive - campo pedidos_ya_id eliminado
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  // Inicializar loadingProduct basado en si hay un producto al abrir
  const [loadingProduct, setLoadingProduct] = useState(open && product ? true : false);
  const [dependenciesLoading, setDependenciesLoading] = useState(false);
  const [references, setReferences] = useState([]);
  // Productos (sin insumos/preparaciones), para cuando Food Operations está
  // apagado: sigue siendo útil poder armar un producto Definido/Indefinido a
  // partir de otros productos, sin que eso dependa del plugin.
  const [productsOnly, setProductsOnly] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]); // Para productos RETAIL
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    priceCurrency: DEFAULT_CURRENCY,
    productType: defaultProductType, // DEFINED, UNDEFINED, RETAIL
    sku: '',
    categoryId: '',
    subCategoryId: '',
    stock: '',
    suggestedStock: '',
    minimumStock: '',
    imageSrc: '',
    alias: '',
    stockControlMode: 'UNITARY',
    usesLot: false,
    ivaRate: 22,
    primarySupplier: null, // Para productos RETAIL
    secondarySupplier: null, // Para productos RETAIL
    packagingItems: [],
    contentItems: [],
    toppingItems: [],
    contentDefinedItems: [],
    contentUndefinedItems: [],
    optionalsItems: [], // Solo UNDEFINED: lista de { referenceType, referenceId1, quantity, unit, extraPrice, displayOrder }
    maxOptionalSelections: '' // Solo UNDEFINED: máximo de opcionales que el cliente puede elegir (vacío = sin límite)
  });

  // Estado para las imágenes múltiples
  const [productImages, setProductImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imagesError, setImagesError] = useState(null);
  
  // Estado para el cropper (cuando se sube una nueva imagen)
  const [imageSrc, setImageSrc] = useState('');
  const [croppedFile, setCroppedFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  // Categorías de producto en forma de Reference, para el picker de "Contenido
  // indefinido" (mode="category"). Se arman acá, a partir de `categories` (que
  // ya se carga siempre, sin depender de ningún plugin), en vez de filtrar
  // `references`: ese array viene de /supplies/all_references, que solo trae
  // datos cuando Food Operations está activo. Las categorías de producto no
  // tienen relación con insumos/preparaciones, así que no deberían depender
  // de ese plugin para poder armar un producto indefinido por categoría.
  const categoryReferences = useMemo(() => (
    (categories || []).flatMap(category => (
      (category.subCategories || []).map(subCategory => ({
        referenceType: referenceTypes.CATEGORIES,
        referenceId1: category.id,
        referenceName1: category.name,
        referenceId2: subCategory.id,
        referenceName2: subCategory.name,
      }))
    ))
  ), [categories]);

  // Productos en forma de Reference, para cuando no se puede cargar el catálogo
  // completo de Food Operations (references). A diferencia de insumos y
  // preparaciones, los productos no son una funcionalidad de ese plugin, así
  // que no tiene sentido ocultarlos solo porque el plugin está apagado.
  const productReferences = useMemo(() => (
    (productsOnly || []).map(prod => ({
      referenceType: referenceTypes.PRODUCTS,
      referenceId1: prod.id,
      referenceName1: prod.alias?.trim() ? prod.alias : prod.name,
      referenceId2: null,
      referenceName2: null,
    }))
  ), [productsOnly]);

  // Con el plugin activo, references ya trae insumos + preparaciones +
  // productos combinados. Sin el plugin, esa llamada ni se hace (ver el
  // useEffect de abajo), así que acá caemos a solo productos.
  const effectiveReferences = canLoadFoodReferences ? references : productReferences;

  // Referencias para opcionales: producto, preparación, insumo (mismo criterio que otros ítems)
  const referencesForOptionals = useMemo(() => (
    (effectiveReferences || []).filter(r =>
      [referenceTypes.INSUMO, referenceTypes.PREPARADO, referenceTypes.PRODUCTS].includes(r.referenceType)
    )
  ), [effectiveReferences]);

  // Opciones para el select "Tipo" en opcionales (Producto, Insumo, Preparación)
  const optionalTypeOptions = useMemo(() => [
    { value: referenceTypes.PRODUCTS, label: t('product') || 'Producto' },
    { value: referenceTypes.INSUMO, label: t('supply') || 'Insumo' },
    { value: referenceTypes.PREPARADO, label: t('preparation') || 'Preparación' },
  ], [t]);

  // Si todas las sucursales facturan en Literal E (todo sin IVA) la tasa de IVA del producto no se usa: se esconde.
  const [hideIvaRate, setHideIvaRate] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    let isMounted = true;
    getAllLocationsLiteralE()
      .then((allLiteralE) => { if (isMounted) setHideIvaRate(allLiteralE); })
      .catch(() => { if (isMounted) setHideIvaRate(false); });
    return () => { isMounted = false; };
  }, [open]);

  // Cargar categorías, referencias y proveedores cuando se abre el modal.
  // Las referencias de insumos son una dependencia exclusiva de Food Operations.
  useEffect(() => {
    if (open) {
      let isMounted = true;
      setDependenciesLoading(true);

      const categoriesPromise = getCategoriesByType(categoryOptions.PRODUCTS)
        .then(categories => {
          if (isMounted) setCategories(categories || []);
        })
        .catch(err => {
          console.error('Error loading categories:', err);
          if (isMounted) setCategories([]);
        });

      const referencesPromise = canLoadFoodReferences
        ? getReferences(locationId, { skipGlobalErrorRedirect: true })
          .then(references => {
            if (isMounted) setReferences(references || []);
          })
          .catch(err => {
            console.error('Error loading references:', err);
            if (isMounted) setReferences([]);
          })
        : Promise.resolve().then(() => {
          if (isMounted) setReferences([]);
        });

      // Sin Food Operations, references queda vacío (arriba). Los productos no
      // son parte de ese plugin, así que los traemos aparte para que Packaging
      // /Contenido/Toppings/Opcionales puedan seguir ofreciéndolos.
      const productsOnlyPromise = !canLoadFoodReferences
        ? getProductsAndPreparationsLight(locationId)
          .then(result => {
            if (isMounted) setProductsOnly(result?.products || []);
          })
          .catch(err => {
            console.error('Error loading products:', err);
            if (isMounted) setProductsOnly([]);
          })
        : Promise.resolve();

      // Cargar proveedores para productos RETAIL
      const suppliersPromise = getSuppliers(1, 10000, '', { skipGlobalErrorRedirect: true }).then(suppliersData => {
        const suppliersArray = Array.isArray(suppliersData) ? suppliersData : (suppliersData?.providers || suppliersData?.data || []);
        // Filtrar solo proveedores que provean productos retail (RETAIL_ONLY o BOTH)
        // Gson serializa enums como strings, así que manejamos ambos casos
        const retailSuppliers = suppliersArray.filter(s => {
          let providerTypeValue = null;
          if (s.providerType) {
            if (typeof s.providerType === 'object' && s.providerType.value) {
              providerTypeValue = s.providerType.value;
            } else if (typeof s.providerType === 'number') {
              providerTypeValue = s.providerType;
            } else if (typeof s.providerType === 'string') {
              // Mapear nombre del enum a valor
              const enumMap = {
                'SUPPLIES_ONLY': 1,
                'RETAIL_ONLY': 2,
                'BOTH': 3
              };
              providerTypeValue = enumMap[s.providerType.toUpperCase()];
            }
          }
          // RETAIL_ONLY (2) o BOTH (3)
          return providerTypeValue === 2 || providerTypeValue === 3;
        });
        if (isMounted) setSuppliers(retailSuppliers);
      }).catch(err => {
        console.error('Error loading suppliers:', err);
        if (isMounted) setSuppliers([]);
      });

      Promise.allSettled([categoriesPromise, referencesPromise, productsOnlyPromise, suppliersPromise])
        .finally(() => {
          if (isMounted) setDependenciesLoading(false);
        });

      return () => {
        isMounted = false;
      };
    }
  }, [open, locationId, canLoadFoodReferences]);

  // Cargar el producto cuando se abre el modal para editar
  useEffect(() => {
    if (open && product) {
      // Inicializar loading inmediatamente antes de cargar
      setLoadingProduct(true);
      // Cargar el producto completo desde el backend
      loadProductForEdit(product.id);
    } else if (open && !product) {
      // Si es un producto nuevo, resetear el formulario
      setLoadingProduct(false);
      resetForm();
    } else if (!open) {
      // Resetear cuando se cierra el modal
      setLoadingProduct(false);
    }
  }, [open, product]);

  // Función para cargar el producto completo para editar
  const loadProductForEdit = async (productId) => {
    try {
      setLoadingProduct(true);
      const fullProduct = await getProductById(productId, locationId);
      
      setFormData({
        name: fullProduct.name,
        price: fullProduct.price,
        priceCurrency: fullProduct.priceCurrency || DEFAULT_CURRENCY,
        productType: fullProduct.productType || 'DEFINED', // DEFINED, UNDEFINED, RETAIL
        sku: fullProduct.sku || '',
        categoryId: fullProduct.category?.id || null,
        subCategoryId: fullProduct.subCategory?.id || null,
        stock: fullProduct.stock,
        suggestedStock: fullProduct.suggestedStock,
        minimumStock: fullProduct.minimumStock,
        imageSrc: '',
        alias: fullProduct.alias || '',
        stockControlMode: resolveStockControlMode(fullProduct),
        usesLot: fullProduct.usesLot === true,
        ivaRate: fullProduct.ivaRate ?? 22,
        primarySupplier: fullProduct.primarySupplier?.id || null,
        secondarySupplier: fullProduct.secondarySupplier?.id || null,
        packagingItems: fullProduct.packaging || [],
        contentItems: fullProduct.content || [],
        toppingItems: fullProduct.toppings || [],
        contentDefinedItems: fullProduct.definedContent || [],
        contentUndefinedItems: fullProduct.undefinedContent || [],
        optionalsItems: (fullProduct.optionals || []).map(o => ({
          referenceType: o.referenceType ?? '',
          referenceId1: o.referenceId1 ?? '',
          quantity: o.quantity ?? 1,
          unit: o.unit ?? 5,
          extraPrice: o.extraPrice ?? '',
          displayOrder: o.displayOrder ?? 0
        })),
        maxOptionalSelections: fullProduct.maxOptionalSelections != null ? String(fullProduct.maxOptionalSelections) : ''
      });

      // Cargar imágenes del producto
      loadProductImages(productId);
    } catch (error) {
      console.error('Error loading product:', error);
      // En caso de error, usar los datos del producto que ya tenemos
      if (product) {
        setFormData({
          name: product.name,
          price: product.price,
          priceCurrency: product.priceCurrency || DEFAULT_CURRENCY,
          productType: product.productType || 'DEFINED',
          sku: product.sku || '',
          categoryId: product.category?.id || null,
          subCategoryId: product.subCategory?.id || null,
          stock: product.stock,
          suggestedStock: product.suggestedStock,
          minimumStock: product.minimumStock,
          imageSrc: '',
          alias: product.alias || '',
          stockControlMode: resolveStockControlMode(product),
          usesLot: product.usesLot === true,
          ivaRate: product.ivaRate ?? 22,
          primarySupplier: product.primarySupplier?.id || null,
          secondarySupplier: product.secondarySupplier?.id || null,
          packagingItems: product.packaging || [],
          contentItems: product.content || [],
          toppingItems: product.toppings || [],
          contentDefinedItems: product.definedContent || [],
          contentUndefinedItems: product.undefinedContent || [],
          optionalsItems: (product.optionals || []).map(o => ({
            referenceType: o.referenceType ?? '',
            referenceId1: o.referenceId1 ?? '',
            quantity: o.quantity ?? 1,
            unit: o.unit ?? 5,
            extraPrice: o.extraPrice ?? '',
            displayOrder: o.displayOrder ?? 0
          })),
          maxOptionalSelections: product.maxOptionalSelections != null ? String(product.maxOptionalSelections) : ''
        });
        loadProductImages(product.id);
      }
    } finally {
      setLoadingProduct(false);
    }
  };

  // Función para cargar las imágenes del producto
  const loadProductImages = async (productId) => {
    try {
      setLoadingImages(true);
      setImagesError(null);
      const images = await ProductImagesService.getProductImages(productId);
      setProductImages(images || []);
    } catch (error) {
      console.error('Error loading product images:', error);
      setImagesError('Error al cargar las imágenes');
      setProductImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  // Eliminado: useEffect para pedidosYaActive - campo pedidos_ya_id eliminado

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      priceCurrency: DEFAULT_CURRENCY,
      productType: defaultProductType,
      sku: '',
      categoryId: '',
      subCategoryId: '',
      stock: '',
      suggestedStock: '',
      minimumStock: '',
      imageSrc: '',
      alias: '',
      stockControlMode: 'UNITARY',
      usesLot: false,
    ivaRate: 22,
      primarySupplier: null,
      secondarySupplier: null,
      packagingItems: [],
      contentItems: [],
      toppingItems: [],
      contentDefinedItems: [],
      contentUndefinedItems: [],
      optionalsItems: [],
      maxOptionalSelections: ''
    });
    setImageSrc('');
    setCroppedFile(null);
    setProductImages([]);
    setLoadingProduct(false);
    setActiveStep(0);
  };

  const steps = useMemo(() => {
    // RETAIL: solo información básica e imagen (no tiene packaging, content, toppings)
    if (formData.productType === 'RETAIL') {
      return [t('basic_information'), t('image')];
    }
    // UNDEFINED: defined_content, undefined_content, optionals
    if (formData.productType === 'UNDEFINED') {
      return [t('basic_information'), t('defined_content'), t('undefined_content'), t('optionals') || 'Opcionales', t('image')];
    }
    // DEFINED: packaging, content, toppings
    return [t('basic_information'), t('packaging'), t('content'), t('toppings'), t('image')];
  }, [formData.productType, t]);

  const dependencyWarnings = useMemo(() => {
    if (product || dependenciesLoading) return [];

    const warnings = [];
    const hasCategories = categories.length > 0;
    const hasSubCategories = categories.some(category => (category.subCategories || []).length > 0);

    if (!hasCategories) {
      warnings.push('Antes de crear un producto es necesario crear una categoría de productos.');
    } else if (!hasSubCategories) {
      warnings.push('Antes de crear un producto es necesario crear una subcategoría dentro de una categoría.');
    }

    if (formData.productType === 'RETAIL' && suppliers.length === 0) {
      warnings.push('Antes de crear un producto es necesario crear un proveedor de productos.');
    }

    return warnings;
  }, [categories, dependenciesLoading, formData.productType, product, suppliers]);

  const hasBlockingDependencies = dependencyWarnings.length > 0;

  const handleNext = () => {
    if (hasBlockingDependencies) return;

    let isValid = true;

    if (activeStep === 0) {
        let errors = {};

        if (!formData.name?.trim()) {
            errors.name = t('name_required');
            isValid = false;
        }
 
        if (formData.stockControlMode !== 'FRACTIONAL') {
          if (!formData.price || formData.price <= 0) {
            errors.price = t('price_required');
            isValid = false;
          }
        }

        if (!formData.categoryId) {
            errors.categoryId = t('category_required');
            isValid = false;
        }

        if (!formData.subCategoryId) {
            errors.subCategoryId = t('subcategory_required');
            isValid = false;
        }

        // Validaciones para productos RETAIL
        if (formData.productType === 'RETAIL') {
          if (!formData.primarySupplier) {
            errors.primarySupplier = t('primary_supplier_required') || 'Proveedor principal es obligatorio';
            isValid = false;
          }
        }

        setFormErrors(errors);

        if (!isValid) return;
    } else {
        const managersToValidate = [
            packagingManagerRef,
            contentManagerRef,
            toppingManagerRef,
            contentDefinedManagerRef,
            contentUndefinedManagerRef
        ].filter(ref => ref.current);

        isValid = managersToValidate.every(ref => ref.current.validateItems());
    }

    if (isValid) setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
      setCroppedFile(file);
    }
  };

  const onCropComplete = useCallback(async (croppedArea, croppedAreaPixels) => {
    try {
      const { file } = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedFile(file);
    } catch (e) {
      console.error(e);
    }
  }, [imageSrc]);

  const handleUploadImage = async () => {
    if (!croppedFile || !product?.id) return;
    
    try {
      setLoadingImages(true);
      const displayOrder = productImages.length;
      const previewFile = await createResizedImageFile(croppedFile, {
        width: 200,
        height: 200,
        mimeType: 'image/webp',
        quality: 0.8,
        fileName: 'preview.webp'
      });
      const newImage = await ProductImagesService.uploadImage(product.id, croppedFile, displayOrder, previewFile);
      setProductImages([...productImages, newImage]);
      setImageSrc('');
      setCroppedFile(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      // Resetear el input file
      const fileInput = document.getElementById('upload-photo');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error uploading image:', error);
      setImagesError('Error al subir la imagen');
    } finally {
      setLoadingImages(false);
    }
  };

  const handleDeleteImage = async (imageId, index) => {
    try {
      setLoadingImages(true);
      await ProductImagesService.deleteImage(imageId, product?.id);
      setProductImages(productImages.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting image:', error);
      setImagesError('Error al eliminar la imagen');
    } finally {
      setLoadingImages(false);
    }
  };

  const handleMoveImage = async (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === productImages.length - 1)) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newImages = [...productImages];
    const temp = newImages[index];
    newImages[index] = newImages[newIndex];
    newImages[newIndex] = temp;

    try {
      setLoadingImages(true);
      // Actualizar el orden en el backend usando los nuevos índices como displayOrder
      await ProductImagesService.updateDisplayOrder(newImages[index].id, index, product?.id);
      await ProductImagesService.updateDisplayOrder(newImages[newIndex].id, newIndex, product?.id);
      setProductImages(newImages);
    } catch (error) {
      console.error('Error moving image:', error);
      setImagesError('Error al reordenar la imagen');
      // Recargar imágenes en caso de error
      if (product?.id) {
        loadProductImages(product.id);
      }
    } finally {
      setLoadingImages(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let response = product
        ? await updateProduct(product.id, formData, locationId)
        : await addProduct(formData, locationId);
      
      // Si hay una imagen nueva recortada y es un producto nuevo, subirla
      if (croppedFile && !product) {
        const previewFile = await createResizedImageFile(croppedFile, {
          width: 200,
          height: 200,
          mimeType: 'image/webp',
          quality: 0.8,
          fileName: 'preview.webp'
        });
        await ProductImagesService.uploadImage(response.id, croppedFile, 0, previewFile);
      } else if (croppedFile && product) {
        // Si es edición y hay imagen nueva, subirla
        const previewFile = await createResizedImageFile(croppedFile, {
          width: 200,
          height: 200,
          mimeType: 'image/webp',
          quality: 0.8,
          fileName: 'preview.webp'
        });
        await ProductImagesService.uploadImage(product.id, croppedFile, productImages.length, previewFile);
      }

      onSave(response);
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{product ? t('edit_product') : t('add_product')}</DialogTitle>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Stepper activeStep={activeStep} sx={{ width: '800px', my: 2 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <DialogContent>
      {loadingProduct ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
      {activeStep === 0 && dependencyWarnings.map((warning) => (
        <Alert key={warning} severity="error" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      ))}
      {activeStep === 0 && (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label={t('name')}
                        required
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label={t('product_alias')}
                        helperText={t('product_alias_help')}
                        value={formData.alias}
                        onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        label={t('sale_price')}
                        required={formData.stockControlMode !== 'FRACTIONAL'}
                        type='number'
                        error={!!formErrors.price}
                        helperText={formErrors.price}
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                        fullWidth
                    />
                    <CurrencySelect
                        value={formData.priceCurrency}
                        onChange={(priceCurrency) => setFormData({ ...formData, priceCurrency })}
                        size="medium"
                        sx={{ minWidth: 150 }}
                    />
                    </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label={t('barcode')}
                        value={formData.sku || ''}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!formErrors.categoryId} required>
                        <InputLabel>{t('category')}</InputLabel>
                        <Select
                            value={formData.categoryId}
                            onChange={(e) => setFormData({
                                ...formData,
                                categoryId: e.target.value,
                                subCategoryId: '' // Resetear subcategoría si cambia la categoría
                            })}
                            label={t('category')}
                        >
                            {categories.map(category => (
                                <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>{formErrors.categoryId}</FormHelperText>
                    </FormControl>
                </Grid>

                {/* Selección de Subcategoría */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!formErrors.subCategoryId} disabled={!formData.categoryId} required>
                        <InputLabel>{t('sub_category')}</InputLabel>
                        <Select
                            value={formData.subCategoryId}
                            onChange={(e) => setFormData({ ...formData, subCategoryId: e.target.value })}
                            label={t('sub_category')}
                        >
                            {categories.find(cat => cat.id === formData.categoryId)?.subCategories.map(subCategory => (
                                <MenuItem key={subCategory.id} value={subCategory.id}>{subCategory.name}</MenuItem>
                            )) || []}
                        </Select>
                        <FormHelperText>{formErrors.subCategoryId}</FormHelperText>
                    </FormControl>
                </Grid>

                {!isRetailCompany && (
                  <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <FormControl fullWidth>
                            <InputLabel>{t('product_type') || 'Tipo de producto'}</InputLabel>
                            <Select
                                value={formData.productType}
                                onChange={(e) => {
                                  const newType = e.target.value;
                                  if (newType === 'RETAIL' && activeStep > 0) {
                                    setActiveStep(0);
                                  }
                                  setFormData({ ...formData, productType: newType });
                                }}
                                label={t('product_type') || 'Tipo de producto'}
                            >
                                <MenuItem value="DEFINED">{t('product_type_defined') || 'Definido'}</MenuItem>
                                <MenuItem value="UNDEFINED">{t('product_type_undefined') || 'Indefinido'}</MenuItem>
                                <MenuItem value="RETAIL">{t('product_type_retail') || 'Retail'}</MenuItem>
                            </Select>
                        </FormControl>
                        <Tooltip
                          title={
                            <span style={{ whiteSpace: 'pre-line' }}>
                              {t('product_type_tooltip') || "Definido: ya está listo para vender. Al venderlo, baja su propio stock.\nIndefinido: se arma al momento de vender. Al venderlo, baja el stock de los ingredientes elegidos.\nRetail: lo comprás terminado y lo revendés. Al venderlo, baja su propio stock."}
                            </span>
                          }
                          arrow
                          placement="right"
                        >
                          <IconButton aria-label={t('product_type_tooltip') || 'Ayuda sobre tipo de producto'} size="small">
                            <InfoOutlinedIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <FormControl fullWidth>
                      <InputLabel id="stock-control-mode-label">{t('stock_control_mode')}</InputLabel>
                      <Select
                        labelId="stock-control-mode-label"
                        label={t('stock_control_mode')}
                        value={formData.stockControlMode}
                        onChange={(e) => setFormData({ ...formData, stockControlMode: e.target.value })}
                      >
                        <MenuItem value="UNITARY">{t('stock_control_mode_unitary')}</MenuItem>
                        <MenuItem value="FRACTIONAL">{t('stock_control_mode_fractional')}</MenuItem>
                      </Select>
                    </FormControl>
                    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('stock_control_mode_help')}</span>} arrow>
                      <IconButton aria-label={t('stock_control_mode_help')} size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>

                {!hideIvaRate && <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <InputLabel id="product-iva-rate-label">{t('product_iva_rate')}</InputLabel>
                        <Select
                            labelId="product-iva-rate-label"
                            label={t('product_iva_rate')}
                            value={formData.ivaRate ?? 22}
                            onChange={(e) => setFormData({ ...formData, ivaRate: Number(e.target.value) })}
                            data-testid="product-iva-rate"
                        >
                            <MenuItem value={22}>{t('iva_rate_basic')}</MenuItem>
                            <MenuItem value={10}>{t('iva_rate_minimum')}</MenuItem>
                            <MenuItem value={0}>{t('iva_rate_exempt')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>}

                {/* Proveedores para productos RETAIL */}
                {formData.productType === 'RETAIL' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!!formErrors.primarySupplier} required>
                        <InputLabel>{t('primary_supplier') || 'Proveedor Principal'}</InputLabel>
                        <Select
                          value={formData.primarySupplier || ''}
                          onChange={(e) => setFormData({ ...formData, primarySupplier: e.target.value })}
                          label={t('primary_supplier') || 'Proveedor Principal'}
                        >
                          {suppliers.map(supplier => (
                            <MenuItem key={supplier.id} value={supplier.id}>
                              {supplier.name} {supplier.businessName ? `- ${supplier.businessName}` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{formErrors.primarySupplier}</FormHelperText>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>{t('secondary_supplier') || 'Proveedor Secundario'}</InputLabel>
                        <Select
                          value={formData.secondarySupplier || ''}
                          onChange={(e) => setFormData({ ...formData, secondarySupplier: e.target.value || null })}
                          label={t('secondary_supplier') || 'Proveedor Secundario'}
                        >
                          <MenuItem value="">{t('none') || 'Ninguno'}</MenuItem>
                          {suppliers.map(supplier => (
                            <MenuItem key={supplier.id} value={supplier.id}>
                              {supplier.name} {supplier.businessName ? `- ${supplier.businessName}` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.usesLot === true}
                        onChange={(event) => setFormData({ ...formData, usesLot: event.target.checked })}
                      />
                    }
                    label="Control de lotes y vencimiento"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Al reponer este producto se solicitarán número de lote y fecha de vencimiento.
                  </Typography>
                </Grid>
          </Grid>
        </>
        )}
        
        {activeStep === 1 && formData.productType !== 'UNDEFINED' && formData.productType !== 'RETAIL' && (
          <ProductItemManager
            ref={packagingManagerRef}
            items={formData.packagingItems}
            setItems={(newItems) => setFormData({ ...formData, packagingItems: newItems })}
            data={effectiveReferences}
          />
        )}

        {activeStep === 1 && formData.productType === 'UNDEFINED' && (
          <ProductItemManager
            ref={contentDefinedManagerRef}
            items={formData.contentDefinedItems}
            setItems={(newItems) => setFormData({ ...formData, contentDefinedItems: newItems })}
            data={effectiveReferences}
          />
        )}

        {activeStep === 2 && formData.productType === 'UNDEFINED' && (
          <ProductItemManager
            ref={contentUndefinedManagerRef}
            items={formData.contentUndefinedItems}
            setItems={(newItems) => setFormData({ ...formData, contentUndefinedItems: newItems })}
            data={categoryReferences}
            mode="category"
        />
        )}

        {activeStep === 3 && formData.productType === 'UNDEFINED' && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>{t('optionals') || 'Opcionales'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('optionals_help') || 'Productos concretos que el cliente puede agregar (ej. salsa de chocolate). Cada uno puede tener costo extra. Defina el máximo que el cliente puede elegir.'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="body1" color="text.primary" sx={{ minWidth: 120 }}>
                {t('max_optional_selections') || 'Máximo a elegir'}
              </Typography>
              <TextField
                type="number"
                size="small"
                inputProps={{ min: 0, step: 1 }}
                value={formData.maxOptionalSelections}
                onChange={(e) => setFormData({ ...formData, maxOptionalSelections: e.target.value })}
                placeholder={t('no_limit') || 'Sin límite'}
                sx={{ width: 100 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => setFormData({ ...formData, optionalsItems: [...formData.optionalsItems, { referenceType: '', referenceId1: '', quantity: 1, unit: 5, extraPrice: '', displayOrder: formData.optionalsItems.length }] })}
              >
                {t('add_optional') || 'Agregar opcional'}
              </Button>
            </Box>
            {formData.optionalsItems.map((opt, idx) => {
              const refsByType = (referencesForOptionals || []).filter((r) => r.referenceType === opt.referenceType);
              const selectedRef = opt.referenceType && opt.referenceId1
                ? refsByType.find((r) => r.referenceId1 === opt.referenceId1)
                : null;
              const unitLabel = selectedRef != null
                ? (getUnitById(selectedRef.unit ?? 5)?.label ?? '—')
                : (opt.unit != null ? (getUnitById(opt.unit)?.label ?? '—') : '—');
              return (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t('type') || 'Tipo'}</InputLabel>
                        <Select
                          value={opt.referenceType ?? ''}
                          label={t('type') || 'Tipo'}
                          onChange={(e) => {
                            const val = e.target.value === '' ? '' : Number(e.target.value);
                            const newItems = [...formData.optionalsItems];
                            newItems[idx] = { ...newItems[idx], referenceType: val, referenceId1: '', unit: val ? 5 : undefined };
                            setFormData({ ...formData, optionalsItems: newItems });
                          }}
                        >
                          <MenuItem value="">{t('select') || 'Seleccionar'}</MenuItem>
                          {optionalTypeOptions.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t('reference') || 'Referencia'}</InputLabel>
                        <Select
                          value={opt.referenceType && opt.referenceId1 ? `${opt.referenceType}-${opt.referenceId1}` : ''}
                          label={t('reference') || 'Referencia'}
                          disabled={!opt.referenceType}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) {
                              const newItems = [...formData.optionalsItems];
                              newItems[idx] = { ...newItems[idx], referenceId1: '', unit: 5 };
                              setFormData({ ...formData, optionalsItems: newItems });
                              return;
                            }
                            const [refType, refId1] = val.split('-').map(Number);
                            const ref = refsByType.find((r) => r.referenceType === refType && r.referenceId1 === refId1);
                            const unitFromRef = ref?.unit ?? 5;
                            const newItems = [...formData.optionalsItems];
                            newItems[idx] = { ...newItems[idx], referenceType: refType, referenceId1: refId1, unit: unitFromRef };
                            setFormData({ ...formData, optionalsItems: newItems });
                          }}
                        >
                          <MenuItem value="">{t('select') || 'Seleccionar'}</MenuItem>
                          {refsByType.map((ref) => (
                            <MenuItem key={`${ref.referenceType}-${ref.referenceId1}`} value={`${ref.referenceType}-${ref.referenceId1}`}>
                              {ref.referenceName1 || `(${ref.referenceType}) ${ref.referenceId1}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ min: 0.01, step: 0.01 }}
                        label={t('quantity') || 'Cantidad'}
                        value={opt.quantity ?? 1}
                        onChange={(e) => {
                          const v = e.target.value === '' ? '' : parseFloat(e.target.value);
                          const newItems = [...formData.optionalsItems];
                          newItems[idx] = { ...newItems[idx], quantity: (v === '' || Number.isNaN(v)) ? 1 : v };
                          setFormData({ ...formData, optionalsItems: newItems });
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('unit') || 'Unidad'}
                        value={unitLabel}
                        disabled
                        sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        label={t('extra_price') || 'Precio extra'}
                        value={opt.extraPrice ?? ''}
                        onChange={(e) => {
                          const newItems = [...formData.optionalsItems];
                          const extraVal = e.target.value === '' ? '' : parseFloat(e.target.value);
                          newItems[idx] = { ...newItems[idx], extraPrice: (extraVal !== '' && Number.isNaN(extraVal)) ? '' : extraVal };
                          setFormData({ ...formData, optionalsItems: newItems });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton size="small" color="error" onClick={() => setFormData({ ...formData, optionalsItems: formData.optionalsItems.filter((_, i) => i !== idx) })} title={t('remove') || 'Quitar'}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Box>
        )}

        {activeStep === 2 && formData.productType !== 'UNDEFINED' && formData.productType !== 'RETAIL' && (
          <ProductItemManager
            ref={contentManagerRef}
            items={formData.contentItems}
            setItems={(newItems) => setFormData({ ...formData, contentItems: newItems })}
            data={effectiveReferences}
          />
        )}

        {activeStep === 3 && formData.productType !== 'UNDEFINED' && formData.productType !== 'RETAIL' && (
          <ProductItemManager
            ref={toppingManagerRef}
            items={formData.toppingItems}
            setItems={(newItems) => setFormData({ ...formData, toppingItems: newItems })}
            data={effectiveReferences}
          />
        )}

        {activeStep === steps.length - 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('images') || 'Imágenes del Producto'}
            </Typography>
            
            {imagesError && (
              <Alert severity="error" onClose={() => setImagesError(null)} sx={{ mb: 2 }}>
                {imagesError}
              </Alert>
            )}

            {/* Galería de imágenes existentes */}
            {product && product.id && (
              <>
                {loadingImages && productImages.length === 0 ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {productImages.map((img, index) => (
                      <Grid item xs={12} sm={6} md={4} key={img.id}>
                        <Card>
                          <CardMedia
                            component="img"
                            height="200"
                            image={img.imageUrl}
                            alt={`Imagen ${index + 1}`}
                            sx={{ objectFit: 'cover' }}
                          />
                          <CardActions sx={{ justifyContent: 'space-between', p: 1 }}>
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveImage(index, 'up')}
                                disabled={index === 0 || loadingImages}
                                title={t('move_up') || 'Mover arriba'}
                              >
                                <ArrowUpwardIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveImage(index, 'down')}
                                disabled={index === productImages.length - 1 || loadingImages}
                                title={t('move_down') || 'Mover abajo'}
                              >
                                <ArrowDownwardIcon />
                              </IconButton>
                            </Box>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteImage(img.id, index)}
                              disabled={loadingImages}
                              title={t('delete') || 'Eliminar'}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}

            {/* Subir nueva imagen */}
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="upload-photo"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="upload-photo">
                <Button 
                  variant="outlined" 
                  color="primary" 
                  component="span" 
                  disabled={loadingImages}
                  sx={{ mb: 2 }}
                >
                  {t('upload_photo') || 'Subir Imagen'}
                </Button>
              </label>
              
              {imageSrc && (
                <Box>
                  <Box sx={{ position: 'relative', width: '100%', height: 300, mb: 2 }}>
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </Box>
                  {product && product.id && (
                    <Button
                      variant="contained"
                      onClick={handleUploadImage}
                      disabled={loadingImages || !croppedFile}
                      sx={{ mr: 1 }}
                    >
                      {loadingImages ? <CircularProgress size={20} /> : (t('save_image') || 'Guardar Imagen')}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setImageSrc('');
                      setCroppedFile(null);
                      setCrop({ x: 0, y: 0 });
                      setZoom(1);
                      const fileInput = document.getElementById('upload-photo');
                      if (fileInput) fileInput.value = '';
                    }}
                  >
                    {t('cancel') || 'Cancelar'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
        </>
      )}
      </DialogContent>
      <DialogActions>
        <Button disabled={activeStep === 0 || loadingProduct} onClick={handleBack}>{t('back')}</Button>
        <LoadingButton loading={loading || loadingProduct} disabled={loadingProduct || (activeStep === 0 && hasBlockingDependencies)} onClick={activeStep === steps.length - 1 ? handleSave : handleNext} variant="contained">
          {activeStep === steps.length - 1 ? t('save') : t('next')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditProductModal;
